import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type BankAccountResponse,
  type ColumnSort,
  filterStatementInvoices,
  formatMoney,
  fromCents,
  type LedgerCycle,
  OPENING_STATE_LABELS,
  STATEMENT_INVOICE_SORTS,
  type StatementExportOptions,
  statementExportSearch,
  type StatementInvoice,
  statementPaymentLabel,
  type StatementSide,
  type StatementStateFilter,
  type SubjectStatementResponse,
  toCents,
} from '@k-management/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { useCan, useCurrentUser } from '../../auth/AuthProvider.js';
import { amountSort, SortHeader, tableSortKey, useTableSort } from '../../components/SortHeader.js';
import { apiDownload, apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { readSessionValue } from '../../lib/preferences.js';
import { ClosureDialog } from './ClosureDialog.js';
import { ReallocationDialog } from './ReallocationDialog.js';
import { StatementFinancialSection } from './StatementFinancial.js';
import { CreditNoteMark, creditNoteRow, InvoiceStatePill } from './treasury-ui.js';
import { SMALL_RESIDUAL_LABEL, WriteOffAllButton } from './WriteOffAllButton.js';

const SIDE_LABELS: Record<LedgerCycle, string> = {
  attivo: 'Come cliente',
  passivo: 'Come fornitore',
};

/** Where the table of the invoices of a side keeps its sort. */
const invoicesSortId = (cycle: LedgerCycle) => `statement.${cycle}.invoices`;

/**
 * The account statement of a subject (09/10/2026), in its sheet and in
 * Tesoreria › Schede contabili: the situation at 1 January from the ledgers,
 * the invoices with what was collected or paid and the movements, the
 * ledgers of the accountant year by year. Read-only.
 */
export function StatementView({ subjectId }: { subjectId: string }) {
  const statement = useQuery({
    queryKey: ['treasury', 'statements', subjectId],
    queryFn: () => apiRequest<SubjectStatementResponse>(`/treasury/statements/${subjectId}`),
  });
  const banks = useQuery({
    queryKey: ['treasury', 'banks'],
    queryFn: () => apiRequest<BankAccountResponse[]>('/treasury/banks'),
  });
  const [cycle, setCycle] = useState<LedgerCycle | null>(null);
  // One filter for both sides: the exports with customer and supplier together use it (2.10).
  const [state, setState] = useState<StatementStateFilter>('all');
  const [together, setTogether] = useState(true);
  const [exportError, setExportError] = useState<string | null>(null);
  // «Riabbina in ordine di data» (2.23): for who may close without a movement.
  const [reallocating, setReallocating] = useState(false);
  const canWriteTreasury = useCan('tesoreria.write');
  const canClose = useCan('tesoreria.closures');
  const canReallocate = canWriteTreasury && canClose;
  const user = useCurrentUser();
  if (statement.isError) return <p className="error">{statement.error.message}</p>;
  if (!statement.data) return <p className="muted">Caricamento…</p>;
  const { sides, financial } = statement.data;
  const bankName = (code: string) => banks.data?.find((bank) => bank.code === code)?.alias ?? code;
  if (sides.length === 0) {
    return (
      <div className="stack">
        <p className="muted">
          Nessuna fattura e nessun mastrino per questo soggetto. Se è nei mastrini, va abbinato in
          Tesoreria › Mastrini.
        </p>
        <StatementFinancialSection financial={financial} bankName={bankName} />
      </div>
    );
  }
  const side = sides.find((item) => item.cycle === cycle) ?? sides[0]!;
  // PDF and Excel (2.10): the side shown, or both; the filter and the sort of the page.
  const exportSearch = () => {
    const cycles = together && sides.length > 1 ? sides.map((item) => item.cycle) : [side.cycle];
    const options: StatementExportOptions = {
      cycles,
      state,
      sorts: Object.fromEntries(
        cycles.map((item) => [
          item,
          readSessionValue<ColumnSort | null>(tableSortKey(invoicesSortId(item), user.email), null),
        ]),
      ),
    };
    return statementExportSearch(options);
  };
  const downloadExcel = async () => {
    setExportError(null);
    try {
      await apiDownload(
        `/treasury/statements/${subjectId}/excel?${exportSearch()}`,
        'Scheda contabile.xlsx',
      );
    } catch (error) {
      setExportError(error instanceof Error ? error.message : String(error));
    }
  };
  const print = () => {
    window.open(
      `/tesoreria/schede-contabili/${subjectId}/stampa?${exportSearch()}&stampa`,
      '_blank',
    );
  };
  return (
    <div className="stack">
      <div className="row between">
        {sides.length > 1 ? (
          <div className="segmented-small" role="group" aria-label="Ruolo">
            {sides.map((item) => (
              <button
                key={item.cycle}
                type="button"
                className={item.cycle === side.cycle ? 'selected' : ''}
                onClick={() => setCycle(item.cycle)}
              >
                {SIDE_LABELS[item.cycle]}
              </button>
            ))}
          </div>
        ) : (
          <span />
        )}
        <div className="row">
          {sides.length > 1 && (
            <label className="checkbox small">
              <input
                type="checkbox"
                checked={together}
                onChange={(event) => setTogether(event.target.checked)}
              />{' '}
              cliente e fornitore insieme
            </label>
          )}
          {canReallocate && (
            <button type="button" onClick={() => setReallocating(true)}>
              Riabbina in ordine di data…
            </button>
          )}
          <button type="button" onClick={() => void downloadExcel()}>
            Excel
          </button>
          <button type="button" className="primary" onClick={print}>
            Stampa o salva PDF
          </button>
        </div>
      </div>
      {exportError && <p className="error">{exportError}</p>}
      <Side key={side.cycle} side={side} bankName={bankName} state={state} onState={setState} />
      <StatementFinancialSection financial={financial} bankName={bankName} />
      {reallocating && (
        <ReallocationDialog
          subjectId={subjectId}
          cycle={side.cycle}
          bankName={bankName}
          onClose={() => setReallocating(false)}
        />
      )}
    </div>
  );
}

function Side({
  side,
  bankName,
  state,
  onState,
}: {
  side: StatementSide;
  bankName: (code: string) => string;
  state: StatementStateFilter;
  onState: (state: StatementStateFilter) => void;
}) {
  const [showOpening, setShowOpening] = useState(false);
  // «Chiudi senza movimento» (2.08), for the CFO.
  const canWrite = useCan('tesoreria.write');
  const mayClose = useCan('tesoreria.closures');
  const canClose = canWrite && mayClose;
  const [closing, setClosing] = useState<StatementInvoice | null>(null);
  const queryClient = useQueryClient();
  const customer = side.cycle === 'attivo';
  const invoices = filterStatementInvoices(side.invoices, state);
  // The same columns the exports sort by.
  const sortedInvoices = useTableSort(
    invoicesSortId(side.cycle),
    invoices,
    STATEMENT_INVOICE_SORTS,
  );
  const sortedOpening = useTableSort(`statement.${side.cycle}.opening`, side.opening?.items ?? [], {
    document: (item) => item.documentDate,
    amount: (item) => amountSort(item.amount),
    open: (item) => amountSort(item.open),
    residual: (item) => amountSort(item.residual),
    settled: (item) => item.settledOn,
  });
  return (
    <div className="stack">
      <div className="treasury-tiles">
        {side.opening && (
          <div className="treasury-tile">
            <span>Saldo al 01/01/{side.opening.year}</span>
            <strong>{formatMoney(side.opening.balance)}</strong>
          </div>
        )}
        <div className="treasury-tile">
          <span>{customer ? 'Fatture emesse' : 'Fatture ricevute'} e partite iniziali</span>
          <strong>{formatMoney(side.totals.invoiced)}</strong>
        </div>
        <div className="treasury-tile">
          <span>{customer ? 'Incassato' : 'Pagato'}</span>
          <strong>{formatMoney(side.totals.settled)}</strong>
        </div>
        <div className="treasury-tile">
          <span>Aperto</span>
          <strong>{formatMoney(side.totals.residual)}</strong>
        </div>
        <div className="treasury-tile">
          <span>di cui scaduto</span>
          <strong className={toCents(side.totals.overdue) > 0 ? 'warn-text' : undefined}>
            {formatMoney(side.totals.overdue)}
          </strong>
        </div>
      </div>

      {side.opening && (
        <div className="card stack tight">
          <div className="row between">
            <strong>
              Situazione al 01/01/{side.opening.year} dai mastrini:{' '}
              {formatMoney(side.opening.balance)}
            </strong>
            <span>
              <span
                className={
                  side.opening.state === 'ricostruito'
                    ? 'pill ok'
                    : side.opening.state === 'da-controllare'
                      ? 'pill warn'
                      : 'pill muted'
                }
              >
                {OPENING_STATE_LABELS[side.opening.state]}
              </span>{' '}
              <button
                type="button"
                className="link small"
                onClick={() => setShowOpening(!showOpening)}
              >
                {showOpening
                  ? 'Nascondi le fatture'
                  : `Fatture aperte (${side.opening.items.length})`}
              </button>
            </span>
          </div>
          {showOpening && (
            <table className="data-table compact">
              <thead>
                <tr>
                  <SortHeader sort={sortedOpening} column="document">
                    Documento
                  </SortHeader>
                  <SortHeader sort={sortedOpening} column="amount" numeric>
                    Importo
                  </SortHeader>
                  <SortHeader sort={sortedOpening} column="open" numeric>
                    Aperto
                  </SortHeader>
                  <SortHeader sort={sortedOpening} column="residual" numeric>
                    Residuo oggi
                  </SortHeader>
                  <SortHeader sort={sortedOpening} column="settled">
                    Nell’anno
                  </SortHeader>
                </tr>
              </thead>
              <tbody>
                {sortedOpening.rows.map((item, index) => (
                  <tr key={index}>
                    <td>
                      {item.kind === 'ante'
                        ? 'Saldo senza dettaglio'
                        : `Doc. ${item.documentNumber ?? '—'}${item.documentDate ? ` del ${formatDate(item.documentDate)}` : ''}`}
                    </td>
                    <td className="numeric">{formatMoney(item.amount)}</td>
                    <td className="numeric">{formatMoney(item.open)}</td>
                    <td className="numeric">
                      {item.residual === null ? '—' : formatMoney(item.residual)}
                    </td>
                    <td>
                      {item.settledOn ? (
                        <span className="pill ok">✓ chiusa il {formatDate(item.settledOn)}</span>
                      ) : (
                        <span className="muted small">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="muted small">
            {side.opening.items.some((item) => item.invoiceId)
              ? 'Situazione fissata: le partite sono anche tra le fatture qui sotto, con i loro incassi e pagamenti.'
              : 'Situazione calcolata dai mastrini: per collegarci incassi e pagamenti va fissata in Tesoreria › Situazione iniziale.'}
          </p>
        </div>
      )}

      <div className="row between">
        <h3>Fatture</h3>
        <div className="row">
          {state === 'small' && <WriteOffAllButton items={invoices} />}
          <select
            className="compact-select"
            aria-label="Stato"
            value={state}
            onChange={(event) => onState(event.target.value as StatementStateFilter)}
          >
            <option value="all">Tutte ({side.invoices.length})</option>
            <option value="open">Da {customer ? 'incassare' : 'pagare'}</option>
            <option value="scaduta">Scadute</option>
            <option value="parziale">Parziali</option>
            <option value="saldata">{customer ? 'Incassate' : 'Pagate'}</option>
            <option value="small">{SMALL_RESIDUAL_LABEL}</option>
          </select>
        </div>
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <SortHeader sort={sortedInvoices} column="number">
                Fattura
              </SortHeader>
              <SortHeader sort={sortedInvoices} column="due">
                Scadenza
              </SortHeader>
              <SortHeader sort={sortedInvoices} column="netDue" numeric>
                Da {customer ? 'incassare' : 'pagare'}
              </SortHeader>
              <SortHeader sort={sortedInvoices} column="paid" numeric>
                {customer ? 'Incassato' : 'Pagato'}
              </SortHeader>
              <SortHeader sort={sortedInvoices} column="residual" numeric>
                Residuo
              </SortHeader>
              <SortHeader sort={sortedInvoices} column="state">
                Stato
              </SortHeader>
              <th>Movimenti</th>
              {canClose && <th aria-label="Azioni" />}
            </tr>
          </thead>
          <tbody>
            {sortedInvoices.rows.map((item) => (
              <tr key={item.id} className={creditNoteRow(item)}>
                <td>
                  <CreditNoteMark kind={item.kind} />
                  <Link to={`/tesoreria/fatture/${item.id}`}>{item.number}</Link>
                  {item.origin === 'apertura' && (
                    <span className="pill info">situazione iniziale</span>
                  )}
                  <small className="muted block">
                    del {formatDate(item.date)}
                    {item.selfBilling && ' · autofattura (fuori dai totali)'}
                  </small>
                </td>
                <td>{item.nextDue ? formatDate(item.nextDue) : '—'}</td>
                <td className="numeric">{formatMoney(item.netDue)}</td>
                <td className="numeric">
                  {formatMoney(fromCents(toCents(item.netDue) - toCents(item.residual)))}
                </td>
                <td className="numeric">
                  <strong>{formatMoney(item.residual)}</strong>
                </td>
                <td>
                  <InvoiceStatePill
                    state={item.state}
                    direction={item.direction}
                    kind={item.kind}
                    replaced={item.replaced}
                    cancelled={item.cancelled}
                    overdueDays={item.overdueDays}
                  />
                </td>
                <td className="small">
                  {item.payments.length === 0
                    ? '—'
                    : item.payments.map((payment, index) => (
                        <span key={index} className="block">
                          {formatDate(payment.date)} · {formatMoney(payment.amount)} ·{' '}
                          {statementPaymentLabel(payment, item, bankName)}
                        </span>
                      ))}
                </td>
                {canClose && (
                  <td className="numeric">
                    {Number(item.residual) !== 0 && (
                      <button type="button" className="link" onClick={() => setClosing(item)}>
                        Chiudi senza movimento…
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr>
                <td colSpan={canClose ? 8 : 7} className="muted">
                  Nessuna fattura con questo filtro.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h3>Mastrini del commercialista</h3>
      {side.ledgers.length === 0 ? (
        <p className="muted small">
          Nessun partitario abbinato a questo soggetto: si abbina in{' '}
          <Link to="/tesoreria/mastrini">Tesoreria › Mastrini</Link>.
        </p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Anno</th>
                <th className="numeric">Saldo iniziale</th>
                <th className="numeric">Dare</th>
                <th className="numeric">Avere</th>
                <th className="numeric">Saldo al 31/12</th>
              </tr>
            </thead>
            <tbody>
              {side.ledgers.map((ledger) => (
                <tr key={ledger.accountId}>
                  <td>{ledger.year}</td>
                  <td className="numeric">{formatMoney(ledger.opening)}</td>
                  <td className="numeric">{formatMoney(ledger.debit)}</td>
                  <td className="numeric">{formatMoney(ledger.credit)}</td>
                  <td className="numeric">
                    <strong>{formatMoney(ledger.closing)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted small">
        Le righe di ogni anno si vedono in{' '}
        <Link to="/tesoreria/mastrini">Tesoreria › Mastrini</Link>. Il confronto di fine anno tra
        K-Management e mastrino arriverà con la verifica, quando fatture e movimenti del periodo
        saranno completi.
      </p>
      {closing && (
        <ClosureDialog
          invoice={closing}
          onClose={() => setClosing(null)}
          onSaved={async () => {
            setClosing(null);
            await queryClient.invalidateQueries({ queryKey: ['treasury'] });
          }}
        />
      )}
    </div>
  );
}
